import { BookMarked } from 'lucide-react';

export type StatuteGridCardProps = {
  /** Uppercase meta line below title (e.g. jurisdiction). */
  metaLabel: string | null;
  title: string;
  /** Bottom-left meta (e.g. section count). */
  footerLabel: string;
  /** Bottom-right pill (e.g. taxonomy category). */
  categoryPill: string | null;
  onClick: () => void;
};

export function StatuteGridCard({
  metaLabel,
  title,
  footerLabel,
  categoryPill,
  onClick,
}: StatuteGridCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="group modern-card relative overflow-hidden rounded-[20px] p-5 min-h-[260px] flex flex-col cursor-pointer hover:-translate-y-0.5 hover:ring-1 hover:ring-primary/10 transition-all duration-200"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-bg-secondary group-hover:bg-primary/5 transition-colors">
          <BookMarked className="w-5 h-5 text-text-muted group-hover:text-primary transition-colors" strokeWidth={2} />
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0 pt-2">
        <p
          className="text-[11px] font-medium uppercase tracking-wider truncate mb-1.5 invisible select-none"
          aria-hidden
        >
          &nbsp;
        </p>
        <h4
          className="font-sans text-xl font-bold leading-snug text-text-main line-clamp-3 group-hover:text-primary transition-colors"
          title={title}
        >
          {title}
        </h4>
        {metaLabel ? (
          <p
            className="mt-1.5 text-[11px] font-medium uppercase tracking-wider text-text-muted truncate"
            title={metaLabel}
          >
            {metaLabel}
          </p>
        ) : null}
        <div className="flex-1 min-h-10" aria-hidden />
      </div>

      <div className="flex items-end justify-between gap-3 pt-3">
        <p className="text-xs text-text-muted truncate min-w-0">{footerLabel}</p>
        {categoryPill ? (
          <span className="inline-flex shrink-0 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary whitespace-nowrap">
            {categoryPill}
          </span>
        ) : null}
      </div>
    </div>
  );
}
